import { nbVal } from "@paroi/data-formatters-lib";
import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { updateTableData } from "../../../../api/application/table-data";
import type { TableDatumInput, TableDatumInputVal } from "../../../../api/domain/table-data";
import { appLog } from "../../../../context";
import type { SchemaField, SchemaFieldOption } from "../../../../helpers/format-databases";
import { transformToFormDataIfFileField } from "../../../../helpers/transform-to-form-data";
import useGetReference from "../../../hooks/useGetReference";
import useGetReferences from "../../../hooks/useGetReferences";
import useGetTableDatum from "../../../hooks/useGetTableDatum";
import useGetTableForm from "../../../hooks/useGetTableForm";
import useGetTables from "../../../hooks/useGetTables";
import { getTableColumnOptions } from "../../../shared/get-table-column-options";
import { showToast } from "../../../shared/global-tools/toast-system";
import { waitFor } from "../../../shared/wait-for";
import Icon from "../../utils/Icons/Icon";
import OurDropdown from "../../utils/OurDropdown/OurDropdown";
import type { FileInputHandle } from "../../utils/SchemaFieldTypeInputs/FileInput/FileInput";
import SchemaFieldInput from "../../utils/SchemaFieldTypeInputs/SchemaFieldInput";
import Spinner from "../../utils/Spinner/Spinner";
import "./index.scss";
import { toCapitalizeFirstLetter } from "../../../../helpers/check-schema-field-type";

// TODO: refactor this, mutualize with DatabaseDataCreatePage

export default function DatabaseDataEditPage() {
  const { databaseId, tableId, datumId } = useParams();
  const databaseIdParam = nbVal(databaseId);
  const tableIdParam = nbVal(tableId);
  const datumIdParam = nbVal(datumId);
  const [selectedTableId, setSelectedTableId] = useState<string>();
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<number>();
  const { references: databases, searchReferences: searchDatabases } = useGetReferences();
  const {
    reference: selectedReference,
    fetchReference,
    loadingReference,
  } = useGetReference({
    referenceId: selectedDatabaseId,
    preloadData: false,
  });
  const { tables, refreshTables, filterTables } = useGetTables({
    referenceId: selectedDatabaseId,
    preloadData: false,
  });
  const wrapAsync = useWrapAsync();
  const { tableForm, loadingTableForm, fetchTableForm } = useGetTableForm();
  const fileInputRef = useRef<FileInputHandle>(null);
  const [inputValues, setInputValues] = useState<TableDatumInput>(new Map());
  const [errorStatus, setErrorStatus] = useState<Map<string, boolean>>(new Map());
  const [savingData, setSavingData] = useState(false);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const { tableDatum, loadingTableDatum, fetchTableDatum } = useGetTableDatum({
    datumId: datumIdParam,
    tableId: tableIdParam,
    referenceId: databaseIdParam,
  });
  const navigate = useNavigate();

  const onChange = ({ name, value }: { name: string; value?: TableDatumInputVal }) => {
    setInputValues((prevValues) => {
      const newValues = new Map(prevValues);
      newValues.set(name, value);
      return newValues;
    });
  };

  const handleSearchDatabases = async (query: string) => {
    await waitFor(500);
    await searchDatabases(query);
    return databases.map((ref) => ({
      value: ref.id,
      label: ref.name,
    }));
  };

  const handleSearchTables = async (query: string) => {
    const filteredTables = filterTables(query);
    return filteredTables.map((table) => ({
      value: table.id,
      label: table.label,
    }));
  };

  const disabledButton = () => {
    for (const [, status] of errorStatus) {
      if (status) {
        return true;
      }
    }
    return false;
  };

  const handleSubmit = wrapAsync(async () => {
    if (!selectedDatabaseId || !selectedTableId) return;
    if (fileInputRef.current && !fileInputRef.current.checkInput()) return;
    if (inputValues.size === 0) return;
    setSavingData(true);
    const values = transformToFormDataIfFileField(
      inputValues,
      tableForm.map((f) => ({
        label: f.label,
        name: f.name,
        type: f.type ?? "string",
        required: !!f.required,
        options: [],
      })),
    );
    try {
      await updateTableData({
        referenceId: selectedDatabaseId,
        tableId: Number(selectedTableId),
        datumId: datumIdParam,
        input: values,
      });
      setInputValues(new Map());
      showToast("Les informations sont mises à jour", { severity: "success" });
      await fetchReference(selectedDatabaseId);
      navigate(`/app/databases/${selectedDatabaseId}/tables/${selectedTableId}`);
    } catch (error) {
      appLog.error(error);
      showToast("Echec mise à jour des informations", { severity: "error", sticky: true });
    } finally {
      setSavingData(false);
    }
  });

  useEffect(
    wrapAsync(async () => {
      if (!selectedDatabaseId) return;
      await fetchReference(selectedDatabaseId);
      await refreshTables(selectedDatabaseId);
    }),
    [selectedDatabaseId],
  );

  useEffect(() => {
    if (!selectedDatabaseId || !selectedTableId) return;
    fetchTableForm({ referenceId: selectedDatabaseId, tableId: Number(selectedTableId) });
  }, [selectedDatabaseId, selectedTableId]);

  useEffect(() => {
    setSelectedDatabaseId(databaseIdParam);
  }, [databaseIdParam]);
  useEffect(() => {
    setSelectedTableId(`${tableIdParam}`);
  }, [tableIdParam]);

  const getSchemaFields = useCallback(
    wrapAsync(async () => {
      if (!tableForm) return;
      const loadedSchemaFields: SchemaField[] = [];
      for (const field of tableForm) {
        let options: SchemaFieldOption[] = [];
        if (!field.type) continue;
        if (field.type === "enum") {
          try {
            options = await getTableColumnOptions(field);
          } catch (error) {
            console.error("Error fetching options for field:", field.name, error);
          }
        }
        loadedSchemaFields.push({
          label: field.label,
          name: field.name,
          type: field.type,
          required: !!field.required,
          options,
          length: field.length,
          default: field.default ? `${field.default}` : undefined,
        });
      }
      setSchemaFields(loadedSchemaFields);
    }),
    [tableForm],
  );
  useEffect(getSchemaFields, [tableForm]);
  useEffect(() => {
    if (!tableDatum || schemaFields.length === 0) {
      return;
    }
    const oldValues = new Map();
    for (const field of Object.keys(tableDatum)) {
      const schemaField = schemaFields.find((f) => f.name === field);
      if (!schemaField) continue;
      const val = tableDatum[field];
      oldValues.set(field, val as TableDatumInputVal);
    }
    setInputValues(oldValues);
  }, [tableDatum, schemaFields]);
  useEffect(
    wrapAsync(async () => {
      if (!selectedDatabaseId || !selectedTableId || !datumIdParam) return;
      await fetchTableDatum(selectedDatabaseId, Number(selectedTableId), datumIdParam);
    }),
    [selectedTableId, datumIdParam, selectedDatabaseId],
  );

  const renderFields = useMemo(() => {
    return schemaFields.map((field) => {
      const fieldValue = inputValues.get(field.name);
      field.label = toCapitalizeFirstLetter(field.label);
      return (
        <SchemaFieldInput
          key={field.name}
          field={field}
          value={fieldValue}
          forwardedRef={fileInputRef}
          onChange={onChange}
          onError={(val) => {
            const oldErrorStatus = new Map(errorStatus);
            oldErrorStatus.set(field.name, val);
            setErrorStatus(oldErrorStatus);
          }}
        />
      );
    });
  }, [errorStatus, inputValues, onChange, schemaFields]);

  if (loadingReference || loadingTableDatum) return <Spinner />;
  return (
    <div className="DatabaseDataEditPage Container">
      <div className="DatabaseDataEditPage-content">
        <div className="DatabaseDataEditPage-header">
          <div className="DatabaseDataEditPage-headerTop">
            <h2 className="DatabaseDataEditPage-title">
              Editer une donnée de {selectedReference?.name || ""}
            </h2>

            {selectedDatabaseId && selectedTableId && (
              <div className="DatabaseDataEditPage-actions">
                <Link
                  to={`/app/databases/${selectedDatabaseId}/tables/${selectedTableId}`}
                  className="Btn blue icon link"
                >
                  <Icon name="database" color="#fff" /> Voir les données
                </Link>

                <Link
                  to={`/app/databases/${selectedDatabaseId}/tables/${selectedTableId}/view`}
                  className="Btn primary icon link"
                >
                  <Icon name="eye" color="#fff" /> Voir la structure
                </Link>
              </div>
            )}
          </div>

          <div className="DatabaseDataEditPage-formRow">
            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Référentiels
              </span>
              <OurDropdown<number>
                initialOptions={databases.map((ref) => ({
                  value: ref.id,
                  label: ref.name,
                }))}
                onSearch={handleSearchDatabases}
                filter
                className="sm RelationCard-dropdown"
                selectedOption={selectedDatabaseId}
                onChange={setSelectedDatabaseId}
                placeholder="Sélectionner"
                disabled={true}
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Table
              </span>
              <OurDropdown<string>
                initialOptions={tables.map((table) => ({
                  value: table.id,
                  label: table.label,
                }))}
                onSearch={handleSearchTables}
                filter
                className="sm RelationCard-dropdown"
                selectedOption={selectedTableId}
                onChange={setSelectedTableId}
                placeholder="Sélectionner"
                // disabled={!selectedDatabaseId}
                disabled={true}
              />
            </label>
          </div>
        </div>

        {selectedDatabaseId && selectedTableId ? (
          <div className="DatabaseDataEditPage-body">
            <div className="DatabaseDataEditPage-bodyContent">
              <h2>Formulaire de saisie</h2>
              <p>Remplissez tous les champs requis (*) pour ajouter un nouvel enregistrement.</p>
            </div>

            <form
              className="DatabaseDataEditPage-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className="DatabaseDataEditPage-formFields">
                {loadingTableForm && <Spinner />}
                {!loadingTableForm && renderFields}
              </div>

              <div className="DatabaseDataEditPage-formActions">
                <p className="DatabaseDataEditPage-formActionsRequired">
                  <span>*</span> Champs obligatoires
                </p>

                <div className="DatabaseDataEditPage-formActionsLeft">
                  <Button
                    className="Btn secondary icon"
                    label="Réinitialiser"
                    icon={<Icon name="reset" />}
                    iconPos="left"
                    onClick={() => setInputValues(new Map())}
                  />
                  <Button
                    type="submit"
                    className="Btn primary icon"
                    label="Enregistrer"
                    icon={<Icon name="save" />}
                    iconPos="left"
                    disabled={savingData || disabledButton()}
                  />
                </div>
              </div>
            </form>
          </div>
        ) : (
          <div className="DatabaseDataEditPage-emptyState">
            <p>Sélectionnez un référentiel et une table pour ajouter un enregistrement.</p>
          </div>
        )}
      </div>
    </div>
  );
}
